import { FC, useEffect, useMemo, useState } from 'react';
import { fetchActivityLogs, ActivityLog } from '../api/activityLogs';
import PaginationBar from '../components/PaginationBar';

const ActivityLogs: FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_LIMIT = 50;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchActivityLogs({ page, limit: PAGE_LIMIT })
      .then((resp) => { if (active) { setLogs(resp.data); setTotal(resp.total); setTotalPages(resp.totalPages); } })
      .catch((err) => { if (active) setError(err instanceof Error ? err.message : 'Failed to load activity logs.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page]);

  const totalPagesMemo = useMemo(() => Math.max(1, totalPages), [totalPages]);

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <h1 className="text-2xl font-semibold text-slate-800 mb-4">Activity Logs</h1>
      <div className="overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">When</th>
              <th className="px-5 py-3 font-semibold">User</th>
              <th className="px-5 py-3 font-semibold">Action</th>
              <th className="px-5 py-3 font-semibold">Entity</th>
              <th className="px-5 py-3 font-semibold">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-5 py-6 text-center text-slate-400" colSpan={5}>Loading…</td></tr>
            ) : error ? (
              <tr><td className="px-5 py-6 text-center text-danger" colSpan={5}>{error}</td></tr>
            ) : logs.length === 0 ? (
              <tr><td className="px-5 py-6 text-center text-slate-400" colSpan={5}>No activity yet.</td></tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 text-slate-500">{new Date(log.createdAt).toLocaleString()}</td>
                  <td className="px-5 py-3 text-slate-700">{log.userName || log.userId || '—'}</td>
                  <td className="px-5 py-3">{log.action}</td>
                  <td className="px-5 py-3">{log.entityType}#{log.entityId}</td>
                  <td className="px-5 py-3 text-slate-500 max-w-[40rem] truncate">{log.details ? JSON.stringify(log.details) : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar
        page={page}
        totalPages={totalPagesMemo}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPagesMemo, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={<span>Showing {(page - 1) * PAGE_LIMIT + Math.min(PAGE_LIMIT, logs.length)} of {total} activities</span>}
      />
    </div>
  );
};

export default ActivityLogs;
